// src/store/slices/notificationsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Notification as ApiNotification, NotificationData, NotificationAction as ApiNotificationAction } from '../../services/notificationService';
import {
  getNotifications,
  markAsRead as markNotificationsReadApi,
  deleteNotification as deleteNotificationApi,
} from '../../services/notificationService';

/** Icon / routing category derived from API `type`. */
export type NotificationUiCategory = 'appointment' | 'message' | 'review' | 'loyalty' | 'system';

export interface Notification {
  id: string;
  userId: string;
  type: NotificationUiCategory;
  /** Raw API notification type (e.g. `appointment_confirmed`). */
  apiType: string;
  title: string;
  message: string;
  read: boolean;
  linkedId?: string;
  createdAt: string;
  data?: NotificationData;
  action?: ApiNotificationAction;
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
};

function extractLinkedId(data?: NotificationData): string | undefined {
  if (!data) return undefined;
  const aid = data.appointmentId as unknown;
  if (typeof aid === 'string') return aid;
  if (aid && typeof aid === 'object' && '_id' in (aid as object)) {
    return String((aid as { _id: string })._id);
  }
  return undefined;
}

function mapApiTypeToUiCategory(type: string): NotificationUiCategory {
  if (type.includes('appointment')) return 'appointment';
  if (type.includes('message') || type.includes('chat')) return 'message';
  if (type.includes('review')) return 'review';
  if (type.includes('loyalty') || type.includes('tier') || type.includes('points')) return 'loyalty';
  return 'system';
}

export function mapApiNotificationToState(n: ApiNotification): Notification {
  const read = n.status !== 'unread';
  return {
    id: n._id,
    userId: typeof n.user === 'string' ? n.user : String((n.user as { _id?: string })?._id ?? ''),
    type: mapApiTypeToUiCategory(n.type),
    apiType: n.type,
    title: n.title,
    message: n.message,
    read,
    linkedId: extractLinkedId(n.data),
    createdAt: n.createdAt,
    data: n.data,
    action: n.action,
  };
}

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchAll',
  async (
    params: { limit?: number; page?: number } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const res = await getNotifications({
        limit: params?.limit ?? 30,
        page: params?.page ?? 1,
      });
      const items = res.data.map(mapApiNotificationToState);
      const unreadCount =
        res.unreadCount ?? items.filter((n) => !n.read).length;
      return { items, unreadCount };
    } catch (error: unknown) {
      const message =
        (error as { message?: string })?.message ||
        (error as { data?: { message?: string } })?.data?.message ||
        'Failed to fetch notifications';
      return rejectWithValue(message);
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId: string, { rejectWithValue }) => {
    try {
      await markNotificationsReadApi([notificationId]);
      return notificationId;
    } catch (error: unknown) {
      const message =
        (error as { message?: string })?.message ||
        'Failed to mark notification as read';
      return rejectWithValue(message);
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { notifications: NotificationsState };
      const unreadIds = state.notifications.notifications
        .filter((n) => !n.read)
        .map((n) => n.id);
      if (unreadIds.length === 0) {
        return { cleared: 0 };
      }
      await markNotificationsReadApi(unreadIds);
      return { cleared: unreadIds.length };
    } catch (error: unknown) {
      const message =
        (error as { message?: string })?.message ||
        'Failed to mark all notifications as read';
      return rejectWithValue(message);
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (notificationId: string, { rejectWithValue }) => {
    try {
      await deleteNotificationApi(notificationId);
      return notificationId;
    } catch (error: unknown) {
      const message =
        (error as { message?: string })?.message ||
        'Failed to delete notification';
      return rejectWithValue(message);
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.read) {
        state.unreadCount += 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchNotifications.fulfilled,
        (state, action: PayloadAction<{ items: Notification[]; unreadCount: number }>) => {
          state.isLoading = false;
          state.notifications = action.payload.items;
          state.unreadCount = action.payload.unreadCount;
        }
      )
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action: PayloadAction<string>) => {
        const notificationIndex = state.notifications.findIndex(
          (n) => n.id === action.payload
        );
        if (notificationIndex !== -1) {
          const wasUnread = !state.notifications[notificationIndex].read;
          state.notifications[notificationIndex].read = true;
          if (wasUnread) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.notifications.forEach((n) => {
          n.read = true;
        });
        state.unreadCount = 0;
      })
      .addCase(deleteNotification.fulfilled, (state, action: PayloadAction<string>) => {
        const notificationIndex = state.notifications.findIndex(
          (n) => n.id === action.payload
        );
        if (notificationIndex !== -1) {
          const wasUnread = !state.notifications[notificationIndex].read;
          state.notifications.splice(notificationIndex, 1);
          if (wasUnread) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
      });
  },
});

export const { addNotification } = notificationsSlice.actions;

export default notificationsSlice.reducer;
